import { useState } from "react";
import { ArrowUp, Loader2, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { UploadBtn } from "@/components/admin/SermonForm";
import { field, card } from "@/components/admin/SettingsPanel";

const slugify = (s) => s.toLowerCase().normalize("NFKD").replace(/[^\w\s-]/g, "").trim().replace(/[\s_]+/g, "-").replace(/-+/g, "-").slice(0, 60) || "department";

// Add, rename, reorder or remove the departments shown under Events/Programs.
export const DepartmentsManager = ({ counts }) => {
  const { settings, setSettings } = useSite();
  const [list, setList] = useState(() => (settings.departments || []).map((d) => ({ ...d })));
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const edit = (i, k) => (e) => setList((p) => p.map((d, j) => (j === i ? { ...d, [k]: e.target.value } : d)));
  const move = (i) => setList((p) => { const n = [...p]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; return n; });
  const remove = (i) => {
    const c = counts[list[i].slug] || 0;
    if (c && !window.confirm(`${c} photo(s)/video(s) are in this department. They will move to "More photos & videos". Remove it?`)) return;
    setList((p) => p.filter((_, j) => j !== i));
  };
  const add = () => {
    const en = name.trim();
    if (!en) return;
    let slug = slugify(en);
    for (let n = 2; list.some((d) => d.slug === slug); n += 1) slug = `${slugify(en)}-${n}`;
    setList((p) => [...p, { slug, en, mh: "", cover_url: "" }]);
    setName("");
  };
  const save = async () => {
    if (list.some((d) => !d.en.trim())) return toast.error("Every department needs an English name");
    setBusy(true);
    try {
      const { data } = await api.put("/settings", { departments: list.map((d) => ({ ...d, en: d.en.trim(), mh: d.mh.trim() })) });
      setSettings((p) => ({ ...p, ...data }));
      toast.success("Departments saved — live on the site");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="departments-manager" className={card}>
      <h3 className="font-display text-2xl text-[#F4EEE2]">Departments</h3>
      <p className="mt-1 text-sm text-[#B7BFCC]">These show in the Events/Programs drop-down menu. Each one has its own page. The cover photo is optional; if you leave it empty, the first photo in the department is used.</p>
      <div className="mt-4 grid gap-3">
        {list.map((d, i) => (
          <div key={d.slug} data-testid={`dept-row-${i}`} className="grid gap-2 rounded-xl border border-[#F4EEE2]/10 p-3 md:grid-cols-[auto_1fr_1fr_auto] md:items-center">
            <div className="h-12 w-16 overflow-hidden rounded-md bg-[#1C2D42]">{d.cover_url && <img src={fileUrl(d.cover_url)} alt="" className="h-full w-full object-cover" />}</div>
            <input data-testid={`dept-en-${i}`} value={d.en} onChange={edit(i, "en")} placeholder="English name" className={field} />
            <input data-testid={`dept-mh-${i}`} value={d.mh} onChange={edit(i, "mh")} placeholder="Marshallese name" className={field} />
            <div className="flex items-center gap-1">
              <UploadBtn accept="image/*" testid={`dept-cover-${i}`} label={d.cover_url ? "Cover" : "Add cover"} onDone={(u) => setList((p) => p.map((x, j) => (j === i ? { ...x, cover_url: u } : x)))} />
              {i > 0 && <button type="button" data-testid={`dept-up-${i}`} onClick={() => move(i)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#D4AF37]" aria-label="Move up"><ArrowUp className="h-4 w-4" /></button>}
              <button type="button" data-testid={`dept-remove-${i}`} onClick={() => remove(i)} className="rounded-full p-2 text-[#B7BFCC] hover:text-[#F2A08A]" aria-label="Remove"><X className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input data-testid="dept-new-name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="New department name (English)" className={field} />
        <button type="button" data-testid="dept-add-btn" onClick={add} className="flex shrink-0 items-center justify-center gap-2 rounded-full border border-[#D4AF37]/50 px-5 py-2.5 font-ui text-sm font-semibold text-[#F4EEE2]"><Plus className="h-4 w-4" /> Add department</button>
      </div>
      <button type="button" data-testid="dept-save-btn" onClick={save} disabled={busy} className="mt-4 flex items-center gap-2 rounded-full bg-[#D4AF37] px-6 py-3 font-ui text-sm font-semibold text-[#0B111E] disabled:opacity-60">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save departments
      </button>
    </div>
  );
};
